import React, { useEffect, useState } from "react";
import Layout from "../components/Layout.jsx";
import { kpiService } from "../services/apiService.jsx";
import * as XLSX from "xlsx";
import FYWeekPicker from "../components/FYWeekPicker"; // [EDITED: KPI Task - Mahesh Sharma]
import { formatFYWeekLabel, formatFYWeekShort } from "../utils/fyWeekUtils"; // [EDITED: KPI Task - Mahesh Sharma]
import "./Kpi.css";
import { Download, Filter, Calendar, TrendingUp, Users, CheckCircle, AlertCircle, XCircle } from "lucide-react";

const KPI = () => {
  const [week, setWeek] = useState("");
  const [kpiData, setKpiData] = useState([]);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    avgTlScore: 0,
    avgHodScore: 0,
    avgMgScore: 0,
    complianceRate: 0
  });

  // ---------------- FETCH WEEKLY KPI ----------------
  useEffect(() => {
    if (!week) return;

    const fetchWeeklyKpi = async () => {
      setLoading(true);
      try {
        // Use kpiService for weekly KPI
        const res = await kpiService.getWeeklyKPI(week);
        const data = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        setKpiData(data);
        // Calculate statistics
        if (data.length > 0) {
          const total = data.length;
          const avgTl = data.reduce((sum, item) => sum + (parseFloat(item.TL_per) || 0), 0) / total;
          const avgHod = data.reduce((sum, item) => sum + (parseFloat(item.hod_per) || 0), 0) / total;
          const avgMg = data.reduce((sum, item) => sum + (parseFloat(item.mg_per) || 0), 0) / total;
          const compliant = data.filter(item =>
            (parseFloat(item.TL_per) || 0) >= 90 &&
            (parseFloat(item.hod_per) || 0) >= 90
          ).length;
          setStats({
            totalEmployees: total,
            avgTlScore: avgTl.toFixed(1),
            avgHodScore: avgHod.toFixed(1),
            avgMgScore: avgMg.toFixed(1),
            complianceRate: ((compliant / total) * 100).toFixed(1)
          });
        }
      } catch (err) {
        console.error("Weekly KPI error:", err);
        setKpiData([]);
        setStats({
          totalEmployees: 0,
          avgTlScore: 0,
          avgHodScore: 0,
          avgMgScore: 0,
          complianceRate: 0
        });
      } finally {
        setLoading(false);
      }
    };
    fetchWeeklyKpi();
  }, [week]);

  // ---------------- KPI COLOR ----------------
  const getColor = (value) => {
    if (value >= 95) return "success";
    if (value >= 90) return "warning";
    return "danger";
  };

  const getColorClass = (value) => {
    if (value >= 95) return "kpi-success";
    if (value >= 90) return "kpi-warning";
    return "kpi-danger";
  };

  const getIcon = (value) => {
    if (value >= 95) return <CheckCircle size={16} />;
    if (value >= 90) return <AlertCircle size={16} />;
    return <XCircle size={16} />;
  };

  // ---------------- DOWNLOAD WEEKLY EXCEL ----------------
  const downloadExcel = () => {
    if (!kpiData.length) return alert("No KPI data available");

    const mappedData = kpiData.map(item => ({
      "Week": formatFYWeekLabel(week),
      "Employee ID": item.employee_code,
      "Employee Name": item.employee_name,
      "Department": item.dept_name,
      "Submission Date": item.submission_date
        ? item.submission_date.split("T")[0]
        : "",
      "TL Approved %": item.TL_per,
      "TL Rating": getColor(item.TL_per).toUpperCase(),
      "HOD Approved %": item.hod_per,
      "HOD Rating": getColor(item.hod_per).toUpperCase(),
      "Management Approved %": item.mg_per,
      "Management Rating": getColor(item.mg_per).toUpperCase(),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(mappedData);
    XLSX.utils.book_append_sheet(wb, ws, formatFYWeekShort(week) || `Weekly_KPI_${week}`);
    
    // Auto-size columns
    const wscols = [
      { wch: 40 }, // Week
      { wch: 12 }, // Employee ID
      { wch: 20 }, // Employee Name
      { wch: 15 }, // Department
      { wch: 15 }, // Submission Date
      { wch: 12 }, // TL %
      { wch: 10 }, // TL Rating
      { wch: 12 }, // HOD %
      { wch: 10 }, // HOD Rating
      { wch: 15 }, // Management %
      { wch: 15 }, // Management Rating
    ];
    ws['!cols'] = wscols;
    
    XLSX.writeFile(wb, `Weekly_KPI_${(formatFYWeekShort(week) || week).replace(/\s+/g, '_')}.xlsx`);
  };

  // ---------------- DOWNLOAD MONTHLY EXCEL ----------------
  const downloadMonthlyExcel = async () => {
    if (!month) return alert("Please select a month");
    try {
      // Use kpiService for monthly KPI
      const res = await kpiService.getMonthlyKPI(month);
      const data = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
      if (!data.length) {
        alert("No data available for selected month");
        return;
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `Monthly_KPI_${month}`);
      XLSX.writeFile(wb, `Monthly_KPI_${month}.xlsx`);
    } catch (err) {
      console.error("Monthly KPI error:", err);
      alert("Failed to download Monthly KPI");
    }
  };

  return (
    <Layout>
      <main id="main" className="main kpi-page">
        <div className="container-fluid py-4">

          {/* PAGE HEADER */}
          <div className="page-header">
            <div className="d-flex justify-content-between align-items-center mb--2">
              <div>
                <h1 className="page-title">KPI Dashboard</h1>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                    <li className="breadcrumb-item active">KPI Analytics</li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  onClick={downloadExcel}
                  disabled={!kpiData.length}
                >
                  <Download size={18} />
                  Weekly Report
                </button>
                <button 
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={downloadMonthlyExcel}
                  disabled={!month}
                >
                  <Download size={18} />
                  Monthly Report
                </button>
              </div>
            </div>
          </div>

          {/* FILTER SECTION */}
          <div className="card filter-card mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 className="card-title d-flex align-items-center gap-2 mb-0">
                    <Filter size={20} />
                    Filter KPI Data
                  </h5>
                  <p className="text-muted mb-0">Select week or month to view performance metrics</p>
                </div>
                <div className="col-md-4">
                  <div className="d-flex gap-3">
                    <div className="flex-grow-1">
                      <label className="form-label small text-muted">Select Week</label>
                      <FYWeekPicker
                        value={week}
                        onChange={setWeek}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* STATISTICS CARDS */}
          {week && (
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title d-flex align-items-center gap-2">
                      <TrendingUp size={20} />
                      Performance Overview ({formatFYWeekLabel(week)}) {/* [EDITED: KPI Task - Mahesh Sharma] */}
                    </h5>
                    <div className="row">
                      <div className="col-6 col-md-2">
                        <div className="stat-card">
                          <h6 className="text-muted">Total Employees</h6>
                          <h3 className="mb-0">{stats.totalEmployees}</h3>
                          <div className="d-flex align-items-center gap-1">
                            <Users size={14} className="text-primary" />
                            <small className="text-muted">Tracked</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="stat-card">
                          <h6 className="text-muted">TL Avg Score</h6>
                          <h3 className={`mb-0 ${getColorClass(stats.avgTlScore)}`}>
                            {stats.avgTlScore}%
                          </h3>
                          <small className="text-muted">Team Lead</small>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="stat-card">
                          <h6 className="text-muted">HOD Avg Score</h6>
                          <h3 className={`mb-0 ${getColorClass(stats.avgHodScore)}`}>
                            {stats.avgHodScore}%
                          </h3>
                          <small className="text-muted">Head of Dept</small>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="stat-card">
                          <h6 className="text-muted">Mgmt Avg Score</h6>
                          <h3 className={`mb-0 ${getColorClass(stats.avgMgScore)}`}>
                            {stats.avgMgScore}%
                          </h3>
                          <small className="text-muted">Management</small>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="stat-card">
                          <h6 className="text-muted">Compliance Rate</h6>
                          <h3 className={`mb-0 ${stats.complianceRate >= 90 ? 'kpi-success' : 'kpi-warning'}`}>
                            {stats.complianceRate}%
                          </h3>
                          <small className="text-muted">Target: 90%+</small>
                        </div>
                      </div>
                      <div className="col-6 col-md-2">
                        <div className="stat-card">
                          <h6 className="text-muted">Status</h6>
                          <div className="d-flex align-items-center gap-2">
                            {loading ? (
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            ) : (
                              <span className={`badge ${kpiData.length ? 'bg-success' : 'bg-secondary'}`}>
                                {kpiData.length ? 'Data Loaded' : 'No Data'}
                              </span>
                            )}
                          </div>
                          <small className="text-muted">Week {week}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* KPI TABLE */}
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">
                  Detailed KPI Analysis
                  {week && <span className="text-muted ms-2 small">Week: {week}</span>}
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <span className="d-flex align-items-center gap-1">
                    <span className="kpi-indicator success"></span>
                    <small>≥ 95%</small>
                  </span>
                  <span className="d-flex align-items-center gap-1">
                    <span className="kpi-indicator warning"></span>
                    <small>90-94%</small>
                  </span>
                  <span className="d-flex align-items-center gap-1">
                    <span className="kpi-indicator danger"></span>
                    <small>&lt; 90%</small>
                  </span>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading KPI data...</p>
                </div>
              ) : (
                <div className="table-responsive-scroll" style={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto" }}>
                  <table className="table table-hover table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>EMP ID</th>
                        <th>Employee Name</th>
                        <th>Department</th>
                        <th>Submission Date</th>
                        <th className="text-center">TL Score</th>
                        <th className="text-center">HOD Score</th>
                        <th className="text-center">Mgmt Score</th>
                        <th className="text-center">Overall Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpiData.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <div className="empty-state">
                              <Calendar size={48} className="text-muted mb-3" />
                              <h5 className="text-muted">
                                {week ? "No KPI data available for selected week" : "Please select a week to view KPI data"}
                              </h5>
                              <p className="text-muted mb-0">Select a week from the filter above to get started</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        kpiData.map((row, i) => (
                          <tr key={i}>
                            <td>
                              <div className="employee-id">{row.employee_code}</div>
                            </td>
                            <td>
                              <div className="employee-name">{row.employee_name}</div>
                            </td>
                            <td>
                              <span className="department-badge">{row.dept_name}</span>
                            </td>
                            <td>
                              <div className="date-cell">
                                {row.submission_date?.split("T")[0]}
                              </div>
                            </td>
                            <td className="text-center">
                              <div className={`score-cell ${getColorClass(row.TL_per)}`}>
                                <div className="score-value">{row.TL_per}%</div>
                                <div className="score-rating">
                                  {getIcon(row.TL_per)}
                                  <small>{getColor(row.TL_per)}</small>
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className={`score-cell ${getColorClass(row.hod_per)}`}>
                                <div className="score-value">{row.hod_per}%</div>
                                <div className="score-rating">
                                  {getIcon(row.hod_per)}
                                  <small>{getColor(row.hod_per)}</small>
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className={`score-cell ${getColorClass(row.mg_per)}`}>
                                <div className="score-value">{row.mg_per}%</div>
                                <div className="score-rating">
                                  {getIcon(row.mg_per)}
                                  <small>{getColor(row.mg_per)}</small>
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="overall-status">
                                <span className={`status-badge ${getColorClass(
                                  Math.max(row.TL_per || 0, row.hod_per || 0, row.mg_per || 0)
                                )}`}>
                                  {getColor(
                                    Math.max(row.TL_per || 0, row.hod_per || 0, row.mg_per || 0)
                                  ).toUpperCase()}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* MONTHLY SECTION */}
          <div className="card mt-4">
            <div className="card-body">
              <h5 className="card-title d-flex align-items-center gap-2 mb-4">
                <Calendar size={20} />
                Monthly KPI Report
              </h5>
              <div className="row align-items-center">
                <div className="col-md-6">
                  <p className="text-muted mb-3">
                    Generate comprehensive monthly performance reports for analysis and review.
                  </p>
                  <div className="input-group" style={{ maxWidth: '300px' }}>
                    <span className="input-group-text">
                      <Calendar size={16} />
                    </span>
                    <input
                      type="month"
                      className="form-control"
                      value={month}
                      onChange={e => setMonth(e.target.value)}
                      placeholder="Select month"
                    />
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <button
                    className="btn btn-success btn-lg d-inline-flex align-items-center gap-2 px-4"
                    onClick={downloadMonthlyExcel}
                    disabled={!month}
                  >
                    <Download size={20} />
                    Download Monthly Report
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </Layout>
  );
};

export default KPI;